import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Check } from 'lucide-react';
import api from '../../services/api';
import Modal from './Modal';

function formatWhatsapp(value) {
  let digits = (value || '').replace(/\D/g, '');
  if (digits.length > 11 && digits.startsWith('55')) digits = digits.slice(2);
  return digits;
}

const TIPO_LABEL = { lead: 'Lead do funil', corretor: 'Corretor cadastrado', gestor: 'Gestor', agenda: 'Agenda da instância', manual: 'Cadastro manual' };

// Colunas `date` do Postgres voltam como timestamp ISO completo (ex: "2026-08-28T00:00:00.000Z"),
// não como "YYYY-MM-DD" puro — sem esse slice, `iso + 'T00:00:00'` vira uma string de data
// inválida (dois "T") e todo cálculo de dia quebra silenciosamente (dias fica vazio).
function soData(iso) {
  return (iso || '').slice(0, 10);
}

// Gera a lista de dias entre data_inicio e data_fim (inclusive) — usado pra deixar quem está
// marcando a confirmação escolher qual dia do evento a pessoa vai comparecer.
function diasDoEvento(evento) {
  if (!evento?.data_inicio) return [];
  const inicio = new Date(soData(evento.data_inicio) + 'T00:00:00');
  const fim = evento.data_fim ? new Date(soData(evento.data_fim) + 'T00:00:00') : inicio;
  const dias = [];
  for (let d = new Date(inicio); d <= fim; d.setDate(d.getDate() + 1)) {
    dias.push(new Date(d).toISOString().slice(0, 10));
  }
  return dias;
}

// Modal reaproveitado em dois lugares: na tela Eventos (evento já fixo, "+ Marcar confirmação")
// e no Funil de Leads Corretores (contato já fixo — o card do lead/corretor —, mas o usuário
// escolhe em qual evento ativo confirmar). Cobre as formas que Fernando pediu: buscar entre leads
// do funil / corretores cadastrados / gestores / agenda da instância (pra quem respondeu vindo de
// um grupo), ou cadastrar direto pelo número de WhatsApp quando não achar ninguém.
export default function ConfirmarPresencaEventoModal({ isOpen, onClose, eventoId, contatoInicial, onConfirmado }) {
  const qc = useQueryClient();
  const [eventoEscolhidoId, setEventoEscolhidoId] = useState(eventoId || '');
  const [contato, setContato] = useState(contatoInicial || null); // {tipo,id,nome,whatsapp} | null (manual)
  const [busca, setBusca] = useState('');
  const [nomeManual, setNomeManual] = useState(contatoInicial?.nome || '');
  const [whatsappManual, setWhatsappManual] = useState(contatoInicial?.whatsapp || '');
  const [instancia, setInstancia] = useState('');
  const [dia, setDia] = useState('');
  const [horario, setHorario] = useState('');
  const [email, setEmail] = useState('');
  const [vinculo, setVinculo] = useState(''); // '' | 'autonomo' | 'imobiliaria'
  const [imobiliaria, setImobiliaria] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setEventoEscolhidoId(eventoId || '');
    setContato(contatoInicial || null);
    setBusca('');
    setNomeManual(contatoInicial?.nome || '');
    setWhatsappManual(contatoInicial?.whatsapp || '');
    setDia('');
    setHorario('');
    setEmail('');
    setVinculo('');
    setImobiliaria('');
    setObservacoes('');
    setErro('');
  }, [isOpen, eventoId, contatoInicial]);

  const precisaEscolherEvento = !eventoId;

  const { data: eventosAtivosData } = useQuery({
    queryKey: ['eventos-ativos-picker'],
    queryFn: () => api.get('/eventos').then(r => r.data),
    enabled: isOpen && precisaEscolherEvento,
  });
  const eventosAtivos = (eventosAtivosData?.data || []).filter(e => e.status === 'ativo');

  const { data: eventoFixoData } = useQuery({
    queryKey: ['evento-detalhe-picker', eventoId],
    queryFn: () => api.get(`/eventos/${eventoId}`).then(r => r.data.data),
    enabled: isOpen && !!eventoId,
  });

  const eventoAtual = eventoId ? eventoFixoData : eventosAtivos.find(e => e.id === eventoEscolhidoId);
  const dias = useMemo(() => diasDoEvento(eventoAtual), [eventoAtual]);

  useEffect(() => {
    if (dias.length === 1 && !dia) setDia(dias[0]);
  }, [dias, dia]);

  const { data: instanciasData } = useQuery({
    queryKey: ['evolution-instancias-eventos'],
    queryFn: () => api.get('/envios-massa/instancias').then(r => r.data),
    enabled: isOpen && !contato,
  });
  const instancias = instanciasData?.data || [];
  useEffect(() => { if (instancias.length && !instancia) setInstancia(instancias[0].name); }, [instancias, instancia]);

  const { data: resultadosBusca, isFetching: buscando } = useQuery({
    queryKey: ['eventos-buscar-contato', busca, instancia],
    queryFn: () => api.get('/eventos/buscar-contato', { params: { q: busca, instancia } }).then(r => r.data.data),
    enabled: isOpen && !contato && busca.trim().length >= 2,
  });

  const confirmarMut = useMutation({
    mutationFn: (payload) => api.post(`/eventos/${eventoAtual.id}/confirmacoes`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['evento-detalhe', eventoAtual.id] });
      qc.invalidateQueries({ queryKey: ['eventos-lista'] });
      onConfirmado?.();
      onClose();
    },
    onError: (err) => setErro(err?.response?.data?.error || 'Erro ao marcar confirmação'),
  });

  const salvar = () => {
    setErro('');
    if (!eventoAtual) return setErro('Selecione o evento');
    const nome = contato ? contato.nome : nomeManual.trim();
    const whatsapp = contato ? contato.whatsapp : whatsappManual.trim();
    if (!nome || !whatsapp) return setErro('Nome e WhatsApp são obrigatórios');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setErro('Informe um e-mail válido');
    if (!vinculo) return setErro('Informe se o corretor é autônomo ou de imobiliária');
    if (vinculo === 'imobiliaria' && !imobiliaria.trim()) return setErro('Informe o nome da imobiliária');
    if (dias.length > 1 && !dia) return setErro('Selecione o dia');
    confirmarMut.mutate({
      nome,
      whatsapp,
      email: email.trim(),
      vinculo_tipo: vinculo,
      imobiliaria_nome: vinculo === 'imobiliaria' ? imobiliaria.trim() : null,
      dia: dia || dias[0] || null,
      horario: horario.trim() || null,
      origem_tipo: contato?.tipo === 'agenda' ? 'manual' : (contato?.tipo || 'manual'),
      origem_id: contato?.id || null,
      observacoes: observacoes.trim() || null,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Marcar confirmação de presença" size="md">
      <div className="space-y-4">
        {precisaEscolherEvento && (
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Evento *</label>
            <select className="input-field" value={eventoEscolhidoId} onChange={e => { setEventoEscolhidoId(e.target.value); setDia(''); }}>
              <option value="">Selecione...</option>
              {eventosAtivos.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </div>
        )}

        {!contatoInicial && (
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Quem confirmou?</label>
            {contato ? (
              <div className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 bg-green-50">
                <div>
                  <div className="font-medium text-sm">{contato.nome}</div>
                  <div className="text-xs text-gray-500">{formatWhatsapp(contato.whatsapp)} · {TIPO_LABEL[contato.tipo] || contato.tipo}</div>
                </div>
                <button onClick={() => setContato(null)} className="text-xs font-medium text-blue-600 hover:text-blue-700">Trocar</button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-3 text-gray-400" />
                  <input
                    className="input-field pl-8"
                    placeholder="Buscar por nome ou WhatsApp (lead, corretor, gestor, agenda)..."
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                  />
                </div>
                {instancias.length > 1 && (
                  <select className="input-field mt-2 text-xs" value={instancia} onChange={e => setInstancia(e.target.value)}>
                    {instancias.map(i => <option key={i.name} value={i.name}>Buscar também na agenda de: {i.name}</option>)}
                  </select>
                )}
                {busca.trim().length >= 2 && (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 mt-2 max-h-40 overflow-y-auto">
                    {buscando ? (
                      <p className="text-xs text-gray-400 px-3 py-2">Buscando...</p>
                    ) : !resultadosBusca?.length ? (
                      <p className="text-xs text-gray-400 px-3 py-2">Ninguém encontrado — preencha manualmente abaixo.</p>
                    ) : resultadosBusca.map(r => (
                      <button
                        key={`${r.tipo}:${r.id || r.whatsapp}`}
                        type="button"
                        onClick={() => setContato(r)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                      >
                        <span className="truncate">{r.nome || 'Sem nome salvo'}</span>
                        <span className="text-xs text-gray-400 shrink-0 ml-2">{TIPO_LABEL[r.tipo]}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">Ou cadastre direto pelo número (quem respondeu num grupo, por exemplo):</p>
                  <div className="grid grid-cols-2 gap-2">
                    <input className="input-field" placeholder="Nome" value={nomeManual} onChange={e => setNomeManual(e.target.value)} />
                    <input className="input-field" placeholder="WhatsApp" value={whatsappManual} onChange={e => setWhatsappManual(formatWhatsapp(e.target.value))} />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {contatoInicial && (
          <div className="border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
            <div className="font-medium text-sm">{contatoInicial.nome}</div>
            <div className="text-xs text-gray-500">{formatWhatsapp(contatoInicial.whatsapp)}</div>
          </div>
        )}

        {dias.length > 1 && (
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Dia *</label>
            <div className="flex gap-2">
              {dias.map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDia(d)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${dia === d ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                >
                  {new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Horário</label>
          <input className="input-field" placeholder="ex: 14h, manhã, 15h30" value={horario} onChange={e => setHorario(e.target.value)} />
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">E-mail *</label>
          <input className="input-field" type="email" placeholder="ex: joao@email.com" value={email} onChange={e => setEmail(e.target.value)} />
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Vínculo do corretor *</label>
          <div className="flex gap-2">
            {[['autonomo', 'Autônomo'], ['imobiliaria', 'De imobiliária']].map(([v, txt]) => (
              <button
                key={v}
                type="button"
                onClick={() => setVinculo(vinculo === v ? '' : v)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${vinculo === v ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              >
                {txt}
              </button>
            ))}
          </div>
          {vinculo === 'imobiliaria' && (
            <input className="input-field mt-2" placeholder="Nome da imobiliária" value={imobiliaria} onChange={e => setImobiliaria(e.target.value)} />
          )}
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Observações</label>
          <textarea className="input-field" rows={2} value={observacoes} onChange={e => setObservacoes(e.target.value)} />
        </div>

        {erro && <p className="text-sm text-red-600">{erro}</p>}

        <div className="flex gap-3 pt-2">
          <button onClick={salvar} disabled={confirmarMut.isPending} className="btn-primary flex items-center gap-2 disabled:opacity-50">
            <Check size={15} /> {confirmarMut.isPending ? 'Salvando...' : 'Marcar confirmação'}
          </button>
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
        </div>
      </div>
    </Modal>
  );
}
